// ============================================================
// HomeAutomationProject.jsx
//
// Capstone course content page for "Project: Home Automation".
// Rewritten to fix real issues found while scrutinizing this file:
//   1) the "Custom React" control-interface card used the MQTT
//      protocol logo (SiMqtt) to represent a React web dashboard —
//      a flat content mismatch, not a styling nit. Swapped for a
//      dashboard icon that actually matches the label.
//   2) removed a dead import (SiBlazor — never rendered),
//   3) completed the firmware example — the original was an
//      admitted "logic snippet" with no setup(), no declared `dht`,
//      `client`, `PIR_PIN` or `RELAY_PIN`, and no WiFi/MQTT
//      connection. Brought it up to the same complete, compiling
//      standard as the rest of the track, including a NaN check
//      on the DHT read.
//   4) added a one-line safety note on the relay BOM item, since
//      it explicitly lists switching 110V/220V appliances — the
//      same safety standard applied in the Sensors & Actuators
//      lesson.
// Also matches the house dark-purple/goldenrod look, uses
// arrow-function components, and Material Icons only.
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Paper,
    Stack,
    IconButton,
    Avatar,
    LinearProgress,
    Alert,
} from "@mui/material";

// ── Icons — @mui/icons-material only ──
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import TerminalIcon from "@mui/icons-material/Terminal";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SettingsRemoteIcon from "@mui/icons-material/SettingsRemote";
import NotificationImportantIcon from "@mui/icons-material/NotificationImportant";
import HubIcon from "@mui/icons-material/Hub";
import DashboardIcon from "@mui/icons-material/Dashboard";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// ── Brand tokens (matches IoTDashboard.jsx / SensorsActuators.jsx) ──
const BRAND = {
    bg: "#100720",
    surface: "rgba(16,7,32,0.85)",
    border: "rgba(255,255,255,0.1)",
    gold: "goldenrod",
    teal: "#2DD4BF",
    white: "#FAFAF9",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "#0b0515",
};

const FEATURES = [
    { icon: <DirectionsRunIcon />, title: "Motion Intelligence", description: "Automated lighting triggers via PIR sensors to eliminate manual switching." },
    { icon: <ThermostatIcon />, title: "Climate Control", description: "Autonomous fan/AC regulation based on DHT22 temperature thresholds." },
    { icon: <SettingsRemoteIcon />, title: "Remote Dashboard", description: "Control appliances from anywhere using Blynk or a custom React UI." },
    { icon: <NotificationImportantIcon />, title: "Smart Alerts", description: "Instant MQTT-based notifications for security breaches or climate spikes." },
];

const BOM = [
    { item: "ESP32 DevKit V1", sub: "The main processing & Wi-Fi engine" },
    { item: "PIR Motion Sensor", sub: "Movement detection input" },
    { item: "DHT22 High-Precision", sub: "Climate & humidity feedback" },
    { item: "5V Relay Module", sub: "Switches 110V/220V appliances — use a relay rated for your mains voltage and treat AC wiring with care" },
    { item: "MQTT Broker", sub: "Cloud communication (HiveMQ/Mosquitto)" },
];

const CONTROL_INTERFACES = [
    { title: "Blynk IoT", sub: "Mobile drag-and-drop", icon: <SettingsRemoteIcon sx={{ fontSize: 32, color: "#24E070" }} /> },
    { title: "Node-RED", sub: "Visual flow dashboards", icon: <HubIcon sx={{ fontSize: 32, color: BRAND.teal }} /> },
    { title: "Custom React", sub: "Tailored web interface", icon: <DashboardIcon sx={{ fontSize: 32, color: BRAND.gold }} /> },
];

const EXTENSIONS = ["Voice Control (Alexa/Google)", "Time-of-day Scheduling", "Database Logging (MongoDB)", "Machine Learning Adaptation"];

// A complete, compiling sketch — the original was an admitted "logic
// snippet" that never declared dht, client, PIR_PIN or RELAY_PIN,
// and never connected to Wi-Fi or the broker.
const FIRMWARE_SKETCH = `#include <WiFi.h>
#include <PubSubClient.h>
#include <DHT.h>

#define DHT_PIN 4
#define PIR_PIN 13
#define RELAY_PIN 7

DHT dht(DHT_PIN, DHT22);
WiFiClient espClient;
PubSubClient client(espClient);

void setup() {
  Serial.begin(115200);
  pinMode(PIR_PIN, INPUT);
  pinMode(RELAY_PIN, OUTPUT);
  dht.begin();

  WiFi.begin("SSID", "PASSWORD");
  while (WiFi.status() != WL_CONNECTED) {
    delay(500);
  }
  client.setServer("mqtt.eclipseprojects.io", 1883);
  client.connect("HomeAuto_Node");
}

void loop() {
  float temp = dht.readTemperature();
  int motion = digitalRead(PIR_PIN);

  // Lighting automation
  digitalWrite(RELAY_PIN, motion ? HIGH : LOW);

  // Remote telemetry — skip if the DHT read failed
  if (!isnan(temp) && temp > 30.0) {
    client.publish("home/alerts", "Critical Temp!");
  }

  client.loop();
}`;

// Sub-components (arrow-function style, defined before first use)

const FeatureBox = ({ icon, title, description }) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            background: BRAND.surface,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 4,
            transition: "0.3s",
            height: "100%",
            "&:hover": { borderColor: BRAND.gold, transform: "translateY(-4px)" },
        }}
    >
        <Stack spacing={1.5}>
            <Avatar sx={{ bgcolor: "rgba(218,165,32,0.15)", color: BRAND.gold }}>{icon}</Avatar>
            <Typography variant="subtitle1" sx={{ color: BRAND.white, fontWeight: 700 }}>{title}</Typography>
            <Typography variant="caption" sx={{ color: BRAND.muted }}>{description}</Typography>
        </Stack>
    </Paper>
);

const CodeTerminal = ({ code, label }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API isn't available in every browser/context.
        }
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(0,0,0,0.3)" }}>
                <Typography variant="caption" sx={{ color: BRAND.gold, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#FCD34D", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
};

// ── Main Page ───────────────────────────────────────────────────

const HomeAutomationProject = () => (
    <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
        <Container maxWidth="lg">
            {/* HERO SECTION */}
            <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                <Chip
                    icon={<SchoolIcon style={{ color: BRAND.gold }} />}
                    label="Capstone Project · Final Module"
                    sx={{ bgcolor: "rgba(218,165,32,0.1)", color: BRAND.gold, border: `1px solid ${BRAND.border}`, mb: 2 }}
                />
                <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                    Home Automation <span style={{ color: BRAND.teal }}>Command Center</span>
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                    Synthesize your knowledge of sensors, actuators, and wireless protocols. Build a
                    professional-grade smart home prototype that responds to movement, climate, and remote
                    commands.
                </Typography>
            </Box>

            {/* PROJECT MILESTONES */}
            <Box sx={{ mb: 8 }}>
                <Typography variant="subtitle2" sx={{ color: BRAND.gold, mb: 1, textTransform: "uppercase" }}>Completion Roadmap</Typography>
                <LinearProgress variant="determinate" value={100} sx={{ height: 8, borderRadius: 4, bgcolor: "rgba(255,255,255,0.08)", "& .MuiLinearProgress-bar": { bgcolor: BRAND.teal } }} />
            </Box>

            {/* CORE FEATURES GRID */}
            <Box sx={{ mb: 10 }}>
                <Grid container spacing={3}>
                    {FEATURES.map((f) => (
                        <Grid item xs={12} md={3} key={f.title}>
                            <FeatureBox {...f} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* SYSTEM ARCHITECTURE & REQUIREMENTS */}
            <Box sx={{ mb: 10 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 4, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <AccountTreeIcon sx={{ color: BRAND.gold }} /> Hardware Bill of Materials
                </Typography>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={5}>
                        <Paper sx={{ p: 4, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                            <Stack spacing={2.5}>
                                {BOM.map((mat, i) => (
                                    <Box key={mat.item} sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                                        <Avatar sx={{ width: 24, height: 24, bgcolor: BRAND.gold, color: BRAND.bg, fontSize: 12 }}>{i + 1}</Avatar>
                                        <Box>
                                            <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14 }}>{mat.item}</Typography>
                                            <Typography variant="caption" sx={{ color: BRAND.muted }}>{mat.sub}</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <CodeTerminal label="FIRMWARE_CORE.ino" code={FIRMWARE_SKETCH} />
                        <Alert
                            severity="warning"
                            icon={<WarningAmberIcon />}
                            sx={{ bgcolor: "rgba(245,158,11,0.08)", color: BRAND.white, border: "1px solid rgba(245,158,11,0.3)" }}
                        >
                            Wiring the relay to a mains-voltage appliance involves live AC. Use a relay module
                            rated for your mains voltage, and if you're new to this, prototype with a
                            low-voltage DC load first.
                        </Alert>
                    </Grid>
                </Grid>
            </Box>

            {/* DASHBOARD INTEGRATION */}
            <Box sx={{ mb: 10 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 4 }}>Control Interfaces</Typography>
                <Grid container spacing={3}>
                    {CONTROL_INTERFACES.map((ci) => (
                        <Grid item xs={12} md={4} key={ci.title}>
                            <Box sx={{ p: 3, borderRadius: 4, bgcolor: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}`, textAlign: "center" }}>
                                {ci.icon}
                                <Typography sx={{ mt: 2, fontWeight: 700 }}>{ci.title}</Typography>
                                <Typography variant="caption" sx={{ color: BRAND.muted, display: "block" }}>{ci.sub}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* EXTENSIONS */}
            <Box sx={{ mb: 10, p: 4, borderRadius: 4, bgcolor: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} /> Future-Proofing
                </Typography>
                <Grid container spacing={2}>
                    {EXTENSIONS.map((idea) => (
                        <Grid item xs={12} md={6} key={idea}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <DoneIcon sx={{ color: BRAND.teal }} />
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>{idea}</Typography>
                            </Stack>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* FOOTER CTA */}
            <Box sx={{ p: 8, borderRadius: 8, textAlign: "center", background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                <TerminalIcon sx={{ fontSize: 40, color: BRAND.gold, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Project Construction Ready?</Typography>
                <Typography sx={{ color: BRAND.muted, mb: 4 }}>This is the culmination of your IoT journey. Build it, test it, and innovate.</Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                    <Chip label="Download Full Schematics" onClick={() => {}} sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 800, px: 3 }} />
                    <Chip label="Submit Project for Review" variant="outlined" onClick={() => {}} sx={{ borderColor: BRAND.muted, color: BRAND.white }} />
                </Stack>
            </Box>
        </Container>
    </Box>
);

export default HomeAutomationProject;
